import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  approveMappingProposal,
  editAutoApprovedMapping,
  listMappingProposals,
  listShipmentStatuses,
  rejectMappingProposal,
} from '../../services/admin-api';

const CATEGORIES = [
  'PRE_TRANSIT',
  'IN_TRANSIT',
  'OUT_FOR_DELIVERY',
  'DELIVERED',
  'EXCEPTION',
  'RETURN',
  'CANCELLED',
  'OTHER',
];

const DEVELOPMENT_ADMIN = process.env.NODE_ENV === 'development'
  ? {
      id: process.env.REACT_APP_DEV_ADMIN_ID || '',
      password: process.env.REACT_APP_DEV_ADMIN_PASSWORD || '',
    }
  : { id: '', password: '' };

const getTodayDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getYesterdayDate = () => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const year = yesterday.getFullYear();
  const month = String(yesterday.getMonth() + 1).padStart(2, '0');
  const day = String(yesterday.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const normalizeCode = (value = '') => value
  .trim()
  .toUpperCase()
  .replace(/[^A-Z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .slice(0, 24);

const formatConfidence = (value) => {
  if (value === null || value === undefined || value === '') return 'Not available';
  const confidence = Number(value);
  return Number.isFinite(confidence) ? `${Math.round(confidence * 100)}%` : 'Not available';
};

const formatLocation = (value) => {
  if (!value) return 'Not supplied';
  if (typeof value === 'string') return value.trim() || 'Not supplied';
  if (typeof value !== 'object' || Array.isArray(value)) return String(value);

  const label = value.label || value.formattedAddress || value.formatted_address || value.address;
  if (typeof label === 'string' && label.trim()) return label.trim();
  const parts = [
    value.city || value.district,
    value.state || value.province,
    value.pincode || value.postalCode || value.zipcode,
    value.country,
  ].filter((part) => part !== null && part !== undefined && String(part).trim());
  return parts.length ? parts.join(', ') : 'Not supplied';
};

function ProposalReviewCard({ proposal, statuses, session, onChanged }) {
  const initialCode = proposal.approvedStatusCode
    || proposal.suggestedStatusCode
    || proposal.predictedCode
    || statuses[0]?.code
    || '';
  const initialCategory = CATEGORIES.includes(proposal.predictedCategory)
    ? proposal.predictedCategory
    : 'OTHER';
  const [approvalMode, setApprovalMode] = useState('MAP_EXISTING');
  const [canonicalStatusCode, setCanonicalStatusCode] = useState(initialCode);
  const [notes, setNotes] = useState('');
  const [newStatus, setNewStatus] = useState({
    code: normalizeCode(proposal.predictedCode || ''),
    name: proposal.predictedName || '',
    description: proposal.sampleDescription || '',
    category: initialCategory,
    lifecycleOrder: 500,
    isTerminal: ['DELIVERED', 'CANCELLED'].includes(initialCategory),
    isException: initialCategory === 'EXCEPTION',
  });
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState('');
  const [editing, setEditing] = useState(false);
  const [editStatusCode, setEditStatusCode] = useState(initialCode);
  const [editNotes, setEditNotes] = useState('');
  const [editApprovalMode, setEditApprovalMode] = useState('MAP_EXISTING');
  const isAutoApproved = proposal.state === 'AUTO_APPROVED';

  const updateNewStatus = (field, value) => {
    setNewStatus((current) => ({ ...current, [field]: value }));
  };

  const saveAutoApprovedEdit = async () => {
    if (editApprovalMode === 'MAP_EXISTING' && !editStatusCode) {
      setActionError('Select an existing shipment status.');
      return;
    }
    if (editApprovalMode === 'CREATE_STATUS'
      && (newStatus.code.length < 2 || newStatus.name.trim().length < 2)) {
      setActionError('New status code and name must contain at least two characters.');
      return;
    }

    setSubmitting(true);
    setActionError('');
    try {
      await editAutoApprovedMapping(
        session.apiKey,
        session.reviewer,
        proposal.predictionId,
        editApprovalMode === 'MAP_EXISTING'
          ? {
              action: 'MAP_EXISTING',
              canonicalStatusCode: editStatusCode,
              ...(editNotes.trim() ? { notes: editNotes.trim() } : {}),
            }
          : {
              action: 'CREATE_STATUS',
              newStatus: {
                code: normalizeCode(newStatus.code),
                name: newStatus.name.trim(),
                description: newStatus.description.trim() || null,
                category: newStatus.category,
                lifecycleOrder: Number(newStatus.lifecycleOrder),
                isTerminal: newStatus.isTerminal,
                isException: newStatus.isException,
              },
              ...(editNotes.trim() ? { notes: editNotes.trim() } : {}),
            },
      );
      setEditing(false);
      await onChanged();
    } catch (error) {
      setActionError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const approve = async () => {
    setActionError('');
    let decision;

    if (approvalMode === 'MAP_EXISTING') {
      if (!canonicalStatusCode) {
        setActionError('Select an existing shipment status.');
        return;
      }
      decision = {
        action: 'MAP_EXISTING',
        canonicalStatusCode,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      };
    } else {
      const code = normalizeCode(newStatus.code);
      if (code.length < 2 || newStatus.name.trim().length < 2) {
        setActionError('New status code and name must contain at least two characters.');
        return;
      }
      decision = {
        action: 'CREATE_STATUS',
        newStatus: {
          code,
          name: newStatus.name.trim(),
          description: newStatus.description.trim() || null,
          category: newStatus.category,
          lifecycleOrder: Number(newStatus.lifecycleOrder),
          isTerminal: newStatus.isTerminal,
          isException: newStatus.isException,
          presentation: {},
        },
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      };
    }

    setSubmitting(true);
    try {
      await approveMappingProposal(
        session.apiKey,
        session.reviewer,
        proposal.id,
        decision,
      );
      await onChanged();
    } catch (error) {
      setActionError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const reject = async () => {
    if (notes.trim().length < 3) {
      setActionError('Enter a rejection reason of at least three characters.');
      return;
    }

    setSubmitting(true);
    setActionError('');
    try {
      await rejectMappingProposal(
        session.apiKey,
        session.reviewer,
        proposal.id,
        notes.trim(),
      );
      await onChanged();
    } catch (error) {
      setActionError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <article className="admin-proposal-card">
      <div className="admin-proposal-card__heading">
        <div>
          <span className={`admin-state admin-state--${proposal.state.toLowerCase()}`}>
            {proposal.state}
          </span>
          <h3>{proposal.carrierName || proposal.carrierCode}</h3>
        </div>
        <span>{proposal.occurrenceCount || 1} occurrence(s)</span>
      </div>

      <dl className="admin-proposal-summary">
        <div><dt>Tracking number</dt><dd>{proposal.awb || 'Not supplied'}</dd></div>
        <div><dt>Carrier status</dt><dd>{proposal.rawStatus || 'Not supplied'}</dd></div>
        <div><dt>Carrier scan</dt><dd>{proposal.rawScan || 'Not supplied'}</dd></div>
        <div><dt>Description</dt><dd>{proposal.sampleDescription || 'Not supplied'}</dd></div>
        <div><dt>Location</dt><dd>{formatLocation(proposal.sampleLocation)}</dd></div>
        <div><dt>AI suggestion</dt><dd>{proposal.predictedName || proposal.predictedCode || 'Not available'}{proposal.predictedName && proposal.predictedCode ? ` (${proposal.predictedCode})` : ''}</dd></div>
        <div><dt>Confidence</dt><dd>{formatConfidence(proposal.confidence)}</dd></div>
        <div><dt>Model</dt><dd>{proposal.modelVersion || 'Not available'}</dd></div>
        <div><dt>Prediction reason</dt><dd>{proposal.modelReason || 'Not supplied'}</dd></div>
      </dl>

      {proposal.state === 'PENDING' ? (
        <div className="admin-proposal-actions">
          <fieldset>
            <legend>Approval action</legend>
            <label>
              <input
                type="radio"
                name={`approval-${proposal.id}`}
                checked={approvalMode === 'MAP_EXISTING'}
                onChange={() => setApprovalMode('MAP_EXISTING')}
              />
              Map to an existing status
            </label>
            <label>
              <input
                type="radio"
                name={`approval-${proposal.id}`}
                checked={approvalMode === 'CREATE_STATUS'}
                onChange={() => setApprovalMode('CREATE_STATUS')}
              />
              Create a new status
            </label>
          </fieldset>

          {approvalMode === 'MAP_EXISTING' ? (
            <label className="admin-field">
              Shipment status
              <select
                value={canonicalStatusCode}
                onChange={(event) => setCanonicalStatusCode(event.target.value)}
              >
                <option value="">Select a status</option>
                {statuses.map((status) => (
                  <option key={status.id || status.code} value={status.code}>
                    {status.name} ({status.code})
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <div className="admin-new-status-grid">
              <label className="admin-field">
                Code
                <input
                  value={newStatus.code}
                  maxLength={24}
                  onChange={(event) => updateNewStatus('code', normalizeCode(event.target.value))}
                />
              </label>
              <label className="admin-field">
                Name
                <input
                  value={newStatus.name}
                  maxLength={120}
                  onChange={(event) => updateNewStatus('name', event.target.value)}
                />
              </label>
              <label className="admin-field admin-field--wide">
                Customer-facing description
                <input
                  value={newStatus.description}
                  maxLength={500}
                  onChange={(event) => updateNewStatus('description', event.target.value)}
                />
              </label>
              <label className="admin-field">
                Category
                <select
                  value={newStatus.category}
                  onChange={(event) => updateNewStatus('category', event.target.value)}
                >
                  {CATEGORIES.map((category) => (
                    <option key={category} value={category}>{category.replaceAll('_', ' ')}</option>
                  ))}
                </select>
              </label>
              <label className="admin-field">
                Lifecycle order
                <input
                  type="number"
                  min="0"
                  max="10000"
                  value={newStatus.lifecycleOrder}
                  onChange={(event) => updateNewStatus('lifecycleOrder', event.target.value)}
                />
              </label>
              <label className="admin-checkbox">
                <input
                  type="checkbox"
                  checked={newStatus.isTerminal}
                  onChange={(event) => updateNewStatus('isTerminal', event.target.checked)}
                />
                Terminal status
              </label>
              <label className="admin-checkbox">
                <input
                  type="checkbox"
                  checked={newStatus.isException}
                  onChange={(event) => updateNewStatus('isException', event.target.checked)}
                />
                Exception status
              </label>
            </div>
          )}

          <label className="admin-field">
            Review notes (required to reject)
            <textarea
              rows="3"
              maxLength={2000}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </label>

          {actionError && <p className="admin-message admin-message--error" role="alert">{actionError}</p>}
          <div className="admin-button-row">
            <button type="button" className="admin-button admin-button--approve" disabled={submitting} onClick={approve}>
              {submitting ? 'Saving…' : 'Approve mapping'}
            </button>
            <button type="button" className="admin-button admin-button--reject" disabled={submitting} onClick={reject}>
              Reject
            </button>
          </div>
        </div>
      ) : isAutoApproved ? (
        <div className="admin-proposal-actions">
          {!editing ? (
            <>
              <p className="admin-review-result">
                Automatically approved by the system.
                <br />
                Current status: {proposal.approvedStatusCode || proposal.predictedCode || 'Not available'}
              </p>
              <button
                type="button"
                className="admin-button"
                disabled={submitting}
                onClick={() => setEditing(true)}
              >
                Edit status
              </button>
            </>
          ) : (
            <>
              <fieldset>
                <legend>Correction action</legend>
                <label>
                  <input
                    type="radio"
                    name={`edit-approval-${proposal.predictionId}`}
                    checked={editApprovalMode === 'MAP_EXISTING'}
                    onChange={() => setEditApprovalMode('MAP_EXISTING')}
                  />
                  Map to an existing status
                </label>
                <label>
                  <input
                    type="radio"
                    name={`edit-approval-${proposal.predictionId}`}
                    checked={editApprovalMode === 'CREATE_STATUS'}
                    onChange={() => setEditApprovalMode('CREATE_STATUS')}
                  />
                  Create a new status
                </label>
              </fieldset>
              {editApprovalMode === 'MAP_EXISTING' ? (
              <label className="admin-field">
                Correct shipment status
                <select
                  value={editStatusCode}
                  onChange={(event) => setEditStatusCode(event.target.value)}
                >
                  <option value="">Select a status</option>
                  {statuses.map((status) => (
                    <option key={status.id || status.code} value={status.code}>
                      {status.name} ({status.code})
                    </option>
                  ))}
                </select>
              </label>
              ) : (
                <div className="admin-new-status-grid">
                  <label className="admin-field">Code<input value={newStatus.code} maxLength={24} onChange={(event) => updateNewStatus('code', normalizeCode(event.target.value))} /></label>
                  <label className="admin-field">Name<input value={newStatus.name} maxLength={120} onChange={(event) => updateNewStatus('name', event.target.value)} /></label>
                  <label className="admin-field admin-field--wide">Customer-facing description<input value={newStatus.description} maxLength={500} onChange={(event) => updateNewStatus('description', event.target.value)} /></label>
                  <label className="admin-field">Category<select value={newStatus.category} onChange={(event) => updateNewStatus('category', event.target.value)}>{CATEGORIES.map((category) => <option key={category} value={category}>{category.replaceAll('_', ' ')}</option>)}</select></label>
                </div>
              )}
              <label className="admin-field">
                Correction notes
                <textarea
                  rows="3"
                  maxLength={2000}
                  value={editNotes}
                  onChange={(event) => setEditNotes(event.target.value)}
                />
              </label>
              {actionError && <p className="admin-message admin-message--error" role="alert">{actionError}</p>}
              <div className="admin-button-row">
                <button
                  type="button"
                  className="admin-button admin-button--approve"
                  disabled={submitting}
                  onClick={saveAutoApprovedEdit}
                >
                  {submitting ? 'Saving…' : 'Save correction'}
                </button>
                <button
                  type="button"
                  className="admin-button"
                  disabled={submitting}
                  onClick={() => setEditing(false)}
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <p className="admin-review-result">
          Reviewed by {proposal.reviewedBy || 'an administrator'}
          {proposal.reviewNotes ? ` — ${proposal.reviewNotes}` : ''}
        </p>
      )}
    </article>
  );
}

export default function AdminReview({ onClose }) {
  const [apiKey, setApiKey] = useState(DEVELOPMENT_ADMIN.password);
  const [reviewer, setReviewer] = useState(DEVELOPMENT_ADMIN.id);
  const [session, setSession] = useState(null);
  const [filter, setFilter] = useState('PENDING');
  const [dateFilter, setDateFilter] = useState(getTodayDate);
  const [customDate, setCustomDate] = useState('');
  const [proposals, setProposals] = useState([]);
  const [total, setTotal] = useState(0);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  const approvedStatuses = useMemo(
    () => statuses.filter((status) => status.isActive !== false && status.approvalState === 'APPROVED'),
    [statuses],
  );

  const loadProposals = useCallback(async (
    activeSession = session,
    activeFilter = filter,
    activeDate = dateFilter,
  ) => {
    if (!activeSession) return;
    const effectiveDate = activeDate === 'CUSTOM'
      ? (customDate || getTodayDate())
      : activeDate;
    setLoading(true);
    setMessage('');
    try {
      const result = await listMappingProposals(
        activeSession.apiKey,
        activeSession.reviewer,
        { state: activeFilter, date: effectiveDate, limit: 100 },
      );
      setProposals(result.items || []);
      setTotal(Number(result.total) || 0);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }, [customDate, dateFilter, filter, session]);

  const connect = async (event) => {
    event.preventDefault();
    if (!apiKey.trim() || reviewer.trim().length < 2) {
      setMessage('Enter the Admin ID and Password.');
      return;
    }

    const nextSession = { apiKey: apiKey.trim(), reviewer: reviewer.trim() };
    setLoading(true);
    setMessage('');
    try {
      const [statusResult, proposalResult] = await Promise.all([
        listShipmentStatuses(nextSession.apiKey, nextSession.reviewer),
        listMappingProposals(nextSession.apiKey, nextSession.reviewer, {
          state: filter,
          date: dateFilter,
          limit: 100,
        }),
      ]);
      setStatuses(statusResult || []);
      setProposals(proposalResult.items || []);
      setTotal(Number(proposalResult.total) || 0);
      setSession(nextSession);
      setApiKey('');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const changeFilter = async (event) => {
    const nextFilter = event.target.value;
    setFilter(nextFilter);
    await loadProposals(session, nextFilter, dateFilter);
  };

  const changeDateFilter = async (event) => {
    const nextDate = event.target.value;
    if (nextDate === 'CUSTOM') {
      setDateFilter('CUSTOM');
      if (customDate) {
        await loadProposals(session, filter, customDate);
      }
      return;
    }
    setDateFilter(nextDate);
    await loadProposals(session, filter, nextDate);
  };

  const changeCustomDate = async (event) => {
    const nextDate = event.target.value;
    setCustomDate(nextDate);
    if (!nextDate) return;
    setDateFilter('CUSTOM');
    await loadProposals(session, filter, nextDate);
  };

  return (
    <div className="admin-review-overlay" role="presentation" onMouseDown={onClose}>
      <section
        className="admin-review-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-review-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="admin-review-header">
          <div>
            <p className="admin-review-eyebrow">Protected administration</p>
            <h2 id="admin-review-title">Shipment status approvals</h2>
          </div>
          <button type="button" className="admin-review-close" aria-label="Close admin panel" onClick={onClose}>
            ×
          </button>
        </header>

        {!session ? (
          <form className="admin-login" onSubmit={connect}>
            <p>Authenticate to review carrier statuses. Credentials stay only in this browser tab until this panel is closed.</p>
            <label className="admin-field">
              Admin ID
              <input
                autoComplete="off"
                value={reviewer}
                onChange={(event) => setReviewer(event.target.value)}
              />
            </label>
            <label className="admin-field">
              Password
              <input
                type="password"
                autoComplete="off"
                value={apiKey}
                onChange={(event) => setApiKey(event.target.value)}
              />
            </label>
            {message && <p className="admin-message admin-message--error" role="alert">{message}</p>}
            <button type="submit" className="admin-button admin-button--approve" disabled={loading}>
              {loading ? 'Checking…' : 'Open approvals'}
            </button>
          </form>
        ) : (
          <div className="admin-review-content">
            <div className="admin-review-toolbar">
              <label className="admin-field admin-filter">
                Proposal state
                <select value={filter} onChange={changeFilter} disabled={loading}>
                  <option value="PENDING">Pending</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="AUTO_APPROVED">Auto-approved</option>
                </select>
              </label>
              <label className="admin-field admin-filter">
                Created date
                <select
                  value={dateFilter === getTodayDate() || dateFilter === getYesterdayDate() || dateFilter === 'ALL' ? dateFilter : 'CUSTOM'}
                  onChange={changeDateFilter}
                  disabled={loading}
                >
                  <option value={getTodayDate()}>Today</option>
                  <option value={getYesterdayDate()}>Yesterday</option>
                  <option value="ALL">All dates</option>
                  <option value="CUSTOM">Custom date</option>
                </select>
              </label>
              {dateFilter === 'CUSTOM' ? (
                <label className="admin-field admin-filter">
                  Select date
                  <input type="date" value={customDate} onChange={changeCustomDate} disabled={loading} />
                </label>
              ) : null}
              <span>{total} proposal(s)</span>
              <button type="button" className="admin-button" disabled={loading} onClick={() => loadProposals(session, filter, dateFilter)}>
                Refresh
              </button>
            </div>

            {message && <p className="admin-message admin-message--error" role="alert">{message}</p>}
            {loading ? (
              <p className="admin-empty-state">Loading proposals…</p>
            ) : proposals.length ? (
              <div className="admin-proposal-list">
                {proposals.map((proposal) => (
                  <ProposalReviewCard
                    key={proposal.id || proposal.predictionId}
                    proposal={proposal}
                    statuses={approvedStatuses}
                    session={session}
                    onChanged={() => loadProposals(session, filter, dateFilter)}
                  />
                ))}
              </div>
            ) : (
              <p className="admin-empty-state">No {filter.toLowerCase()} proposals found.</p>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
